/**
 * Confetti utility function
 * Triggers confetti animation from a specific element's position
 */

// Declare global confetti type
declare global {
  interface Window {
    confetti: (options: ConfettiOptions) => void;
  }
}

interface ConfettiOptions {
  angle?: number;
  spread?: number;
  particleCount?: number;
  origin?: { x?: number; y?: number };
  colors?: string[];
}

/**
 * Generates a random number between min and max (inclusive)
 */
function randomInRange(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

/**
 * Triggers confetti animation from a specific element's position
 * @param element - The HTML element from which to trigger confetti
 * @param colors - Optional array of colors for the confetti particles
 */
export function triggerConfettiFromElement(element: HTMLElement | null, colors?: string[]): void {
  if (!element || typeof window === 'undefined' || !window.confetti) {
    console.warn('Confetti library not available or element not found');
    return;
  }

  // Get element's bounding rectangle
  const rect = element.getBoundingClientRect();
  
  // Calculate the center of the element
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  
  // Normalize to viewport coordinates (0-1 range)
  const originX = centerX / window.innerWidth;
  const originY = centerY / window.innerHeight;

  // Build confetti options
  const confettiOptions: ConfettiOptions = {
    angle: randomInRange(55, 125),
    spread: randomInRange(50, 70),
    particleCount: randomInRange(50, 100),
    origin: { 
      x: originX,
      y: originY 
    },
  };

  // Add colors if provided
  if (colors && colors.length > 0) {
    confettiOptions.colors = colors;
  }

  // Trigger confetti with parameters
  window.confetti(confettiOptions);
}

/**
 * Triggers green confetti animation from a specific element's position
 * @param element - The HTML element from which to trigger confetti
 */
export function triggerGreenConfettiFromElement(element: HTMLElement | null): void {
  // Green color palette from secondary colors
  const greenColors = ['#22c55e', '#16a34a', '#4ade80', '#86efac'];
  triggerConfettiFromElement(element, greenColors);
}

